import React, { createContext, useContext, useEffect, useState } from "react";
import { attendanceService } from "../services/api";
import { getAuthItem } from "../utils/authStorage";

const AttendanceCodeConfigContext = createContext(null);

export const AttendanceCodeConfigProvider = ({ children }) => {
  const [mapping, setMapping] = useState({});
  const [reverseMapping, setReverseMapping] = useState({});
  const [exportMapping, setExportMapping] = useState({});
  const [importMapping, setImportMapping] = useState({});
  const [importReverseMapping, setImportReverseMapping] = useState({});
  const [meaningByCode, setMeaningByCode] = useState({});
      // Half-day export format (persisted on company row): empty = no divider/prefix on import or export files
      const [halfDayDivider, setHalfDayDivider] = useState("");
      const [halfDayPrefix, setHalfDayPrefix] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const buildMappings = (configs = []) => {
    // configs: [{ meaning, originalCode|original_code|original, customCode|custom_code|custom, exportCode|export_code, importCode|import_code }]
    const m = {};
    const r = {};
    const e = {};
    const im = {}; // import mapping: original -> import code
    const imr = {}; // import reverse mapping: import code -> original
    const meaningByCode = {}; // original code -> meaning (for Legend, no hardcoded labels)
    configs.forEach((c) => {
      const backend = String(
        c.originalCode || c.original || c.original_code || ""
      ).toUpperCase();
      const custom = String(
        c.customCode || c.custom || c.custom_code || backend || ""
      ).toUpperCase();
      const exportCode = String(
        c.exportCode || c.export_code || backend || ""
      ).toUpperCase();
      const importCode = String(
        c.importCode || c.import_code || backend || ""
      ).toUpperCase();
      const meaning = c.meaning || backend || custom;
      if (backend) m[backend] = custom;
      if (custom) r[custom] = backend;
      if (backend) e[backend] = exportCode;
      if (backend) im[backend] = importCode;
      if (importCode) imr[importCode] = backend;
      if (backend && meaning) meaningByCode[backend] = meaning;
      if (custom && custom !== backend && meaning) meaningByCode[custom] = meaning;
    });
    setMapping(m);
    setReverseMapping(r);
    setExportMapping(e);
    setImportMapping(im);
    setImportReverseMapping(imr);
    setMeaningByCode(meaningByCode);
    try {
      // Log mapping changes so they are visible in standard Console
      console.log("AttendanceCodeConfig - buildMappings", {
        mappings: m,
        reverseMapping: r,
        sourceCount: configs.length,
      });
    } catch (e) {
      console.error("AttendanceCodeConfig - buildMappings log failed", e);
    }
  };

  const DEFAULT_CONFIGS = [
    { meaning: "Present", originalCode: "PR", customCode: "PR", importCode: "PR", exportCode: "PR" },
    { meaning: "Absent", originalCode: "LOP", customCode: "LP", importCode: "LP", exportCode: "LP" },
    { meaning: "Weekly Off", originalCode: "WO", customCode: "WO", importCode: "WO", exportCode: "WO" },
    { meaning: "Public Holiday", originalCode: "PH", customCode: "PH", importCode: "PH", exportCode: "PH" },
  ];

  const fetchConfigurations = async () => {
    try {
      setLoading(true);

      // 🔧 FIX: Check if user is authenticated first (proxy-aware)
      const token = getAuthItem("token");
      const userType = getAuthItem("userType");

      if (!token || !userType) {
        console.log(
          "AttendanceCodeConfig - No auth token, using DEFAULT_CONFIGS"
        );
        buildMappings(DEFAULT_CONFIGS);
        setLoading(false);
        return DEFAULT_CONFIGS;
      }

      // 🔧 FIX: Check if user is ADMIN (only admins can access configs)
      if (userType !== "admin") {
        console.log(
          "AttendanceCodeConfig - User is not admin, using DEFAULT_CONFIGS"
        );
        buildMappings(DEFAULT_CONFIGS);
        setLoading(false);
        return DEFAULT_CONFIGS;
      }

      const resp = await attendanceService.getConfigurations();
      // Expect resp to be array or { codes: [...], data?: [...], halfDayDivider?, halfDayPrefix? }
      const configs = Array.isArray(resp)
        ? resp
        : resp?.codes ||
          resp?.configurations ||
          resp?.rows ||
          resp?.savedRows ||
          resp?.data?.rows ||
          resp?.data ||
          [];
      // Half-day fields: API returns null when unset — show empty inputs (no localStorage override)
      if (resp && typeof resp === "object" && !Array.isArray(resp)) {
        setHalfDayDivider(
          resp.halfDayDivider != null && String(resp.halfDayDivider).trim() !== ""
            ? String(resp.halfDayDivider).trim()
            : ""
        );
        setHalfDayPrefix(
          resp.halfDayPrefix != null && String(resp.halfDayPrefix).trim() !== ""
            ? String(resp.halfDayPrefix).trim()
            : ""
        );
      }
      // If backend returned nothing, fall back to defaults so frontend validation works
      if (!Array.isArray(configs) || configs.length === 0) {
        console.warn(
          "AttendanceCodeConfig - no configs returned from server, using DEFAULT_CONFIGS fallback"
        );
        buildMappings(DEFAULT_CONFIGS);
        setLoading(false);
        return DEFAULT_CONFIGS;
      }
      buildMappings(configs);
      setLoading(false);
      return configs;
    } catch (err) {
      setError(err);
      // On error, ensure we still expose defaults so frontend doesn't break validation
      console.warn(
        "AttendanceCodeConfig - failed to fetch configurations, using DEFAULT_CONFIGS fallback",
        err
      );
      buildMappings(DEFAULT_CONFIGS);
      setLoading(false);
      return DEFAULT_CONFIGS;
    }
  };

  // NOTE: removed optimistic/manual mapping setter to avoid overriding server data.

  const saveConfigurations = async (configsOrPayload) => {
    try {
      setLoading(true);
      // Accept array (legacy) or { codes, halfDayDivider?, halfDayPrefix? }
      const isObject = configsOrPayload && typeof configsOrPayload === "object" && !Array.isArray(configsOrPayload);
      const codes = isObject ? configsOrPayload.codes : configsOrPayload;
      const payload = {
        codes: Array.isArray(codes)
          ? codes.map((c) => ({
              meaning: c.meaning,
              customCode: c.customCode,
              importCode: c.importCode,
              exportCode: c.exportCode,
            }))
          : [],
      };
      if (isObject && "halfDayDivider" in configsOrPayload) {
        const d = configsOrPayload.halfDayDivider;
        payload.halfDayDivider =
          d != null && String(d).trim() !== "" ? String(d).trim() : null;
      }
      if (isObject && "halfDayPrefix" in configsOrPayload) {
        const p = configsOrPayload.halfDayPrefix;
        payload.halfDayPrefix =
          p != null && String(p).trim() !== "" ? String(p).trim() : null;
      }
      const resp = await attendanceService.saveConfigurations(payload);
      if (resp && typeof resp === "object" && !Array.isArray(resp)) {
        if ("halfDayDivider" in resp) {
          setHalfDayDivider(
            resp.halfDayDivider != null && String(resp.halfDayDivider).trim() !== ""
              ? String(resp.halfDayDivider).trim()
              : ""
          );
        }
        if ("halfDayPrefix" in resp) {
          setHalfDayPrefix(
            resp.halfDayPrefix != null && String(resp.halfDayPrefix).trim() !== ""
              ? String(resp.halfDayPrefix).trim()
              : ""
          );
        }
      }
      const saved = Array.isArray(resp)
        ? resp
        : resp?.codes ||
          resp?.configurations ||
          resp?.rows ||
          resp?.savedRows ||
          resp?.data?.rows ||
          (Array.isArray(codes) ? codes : []);
      // Re-fetch authoritative configs from server to ensure provider state matches backend
      try {
        const fresh = await fetchConfigurations();
        // fetchConfigurations already calls buildMappings
        window.dispatchEvent(new Event("attendanceMappingsUpdated"));
      } catch (e) {
        console.warn("Failed to refresh configurations after save", e);
        // fallback to local build
        buildMappings(saved);
        try {
          window.dispatchEvent(new Event("attendanceMappingsUpdated"));
        } catch (evErr) {
          console.warn(
            "Failed to dispatch attendanceMappingsUpdated event",
            evErr
          );
        }
      }
      setLoading(false);
      return resp;
    } catch (err) {
      setError(err);
      setLoading(false);
      throw err;
    }
  };

  const resetConfigurations = async () => {
    try {
      setLoading(true);
      const resp = await attendanceService.resetConfigurations();
      const configs = Array.isArray(resp)
        ? resp
        : resp?.data ??
          resp?.codes ??
          resp?.configurations ??
          resp?.rows ??
          [];
      if (resp && typeof resp === "object" && !Array.isArray(resp)) {
        setHalfDayDivider(
          resp.halfDayDivider != null && String(resp.halfDayDivider).trim() !== ""
            ? String(resp.halfDayDivider).trim()
            : ""
        );
        setHalfDayPrefix(
          resp.halfDayPrefix != null && String(resp.halfDayPrefix).trim() !== ""
            ? String(resp.halfDayPrefix).trim()
            : ""
        );
      } else {
        setHalfDayDivider("");
        setHalfDayPrefix("");
      }
      // Re-fetch authoritative configs from server after reset
      try {
        await fetchConfigurations();
        window.dispatchEvent(new Event("attendanceMappingsUpdated"));
      } catch (e) {
        console.warn("Failed to refresh configurations after reset", e);
        buildMappings(configs);
        try {
          window.dispatchEvent(new Event("attendanceMappingsUpdated"));
        } catch (evErr) {
          console.warn(
            "Failed to dispatch attendanceMappingsUpdated event",
            evErr
          );
        }
      }
      setLoading(false);
      return resp;
    } catch (err) {
      setError(err);
      setLoading(false);
      throw err;
    }
  };

  useEffect(() => {
    fetchConfigurations();
  }, []);

  return (
    <AttendanceCodeConfigContext.Provider
      value={{
        mapping,
        reverseMapping,
        exportMapping,
        importMapping,
        importReverseMapping,
        meaningByCode,
        halfDayDivider,
        halfDayPrefix,
        loading,
        error,
        fetchConfigurations,
        saveConfigurations,
        resetConfigurations,
      }}
    >
      {children}
    </AttendanceCodeConfigContext.Provider>
  );
};

export const useAttendanceCodeConfig = () => {
  const ctx = useContext(AttendanceCodeConfigContext);
  if (!ctx)
    throw new Error("useAttendanceCodeConfig must be used within provider");
  return ctx;
};

export default AttendanceCodeConfigContext;
